import React, {Dispatch, useEffect, useState} from 'react';
import Select from 'react-select';
import {connect} from 'react-redux';

import {Choice, WorkflowItem} from '../types';

import {Modal} from './Modal';
import {RadioButton} from './RadioButton';
import {ActivityButton} from './ActivityButton';

import {Flow, FlowFetchResponse} from '../_services/Flow';
import {getFeedbackModalIsShowing} from '../_selectors/feedback';
import {hideFeedbackModal, showFeedbackModal} from '../_actions/feedback';
import {useHistory} from 'react-router-dom';

type FeedbackProps = {
    isShowingModal: boolean;
    dispatchFeedbackShowModal: () => void;
    dispatchFeedbackHideModal: () => void;
};

type FormState = {
    helpType: '' | 'general' | 'workflow';
    workflow: null | Choice;
    message: string;
};

const initialFormState: FormState = {
    helpType: '',
    workflow: null,
    message: ''
};

const Feedback: React.FunctionComponent<FeedbackProps> = ({
                                                              isShowingModal,
                                                              dispatchFeedbackHideModal
                                                          }) => {
    const history = useHistory();

    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);
    const [workflows, setWorkflows] = useState<any>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // get list of workflows
    useEffect(() => {
        Flow.getInstance().getWorkflows()
            .then((response: FlowFetchResponse) => {
                setWorkflows(
                    response.json.workflows
                        .sort((first: WorkflowItem, second: WorkflowItem) => first.name.localeCompare(second.name))
                        .map((workflow: WorkflowItem) => ({
                            value: workflow.id,
                            label: workflow.name,
                            text: workflow.name
                        }))
                );
            })
            .catch((error: any) => {
                console.log(error);
            });

    }, [false]);

    const handleChange = (fieldName: string) => (value: any) => {
        setFormState({
            ...formState,
            [fieldName]: (value instanceof Object && value.hasOwnProperty('target')) ? value.target.value : value
        });
    };

    const canSubmit = (
        (
            formState.helpType === 'workflow' &&
            formState?.workflow?.hasOwnProperty('value') &&
            formState.workflow.value &&
            formState.message
        ) || (
            formState.helpType === 'general' &&
            formState.message
        )
    );

    const sendHelp = () => {
        setFormIsSubmitting(true);
        setErrorMessage('');

        Flow.getInstance().submitFeedback(formState?.helpType || 'general', formState?.workflow?.value || '', formState.message)
            .then((response: FlowFetchResponse) => {
                setFormIsSubmitting(false);
                if (!response.ok) {
                    setErrorMessage(response.json.message || 'Error submitting Feedback. Please try again.');
                } else {
                    dispatchFeedbackHideModal();
                    setFormState(initialFormState);
                }
            })
            .catch((error: any) => {
                setFormIsSubmitting(false);
                setErrorMessage(error.message || error || 'Exception submitting Feedback. Please try again.');
            });
    };

    return (
        <div>
            <button className="btn btn-secondary d-flex flex-column justify-content-around"
                    id="feedback-button"
                    onClick={() => {
                        history.push('/support');
                    }}
            >
                <span className="text">Support</span>
            </button>
            {isShowingModal && <Modal>
                <div className="modal-header">
                    <h5 className="modal-title">What do you need Help with?</h5>
                    <button type="button"
                            className="close"
                            tabIndex={-1}
                            onClick={dispatchFeedbackHideModal}
                    >×
                    </button>
                </div>
                <div className="modal-body">
                    {errorMessage && <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>}
                    <form className="text-center">
                        <div className="form-group">
                            <RadioButton
                                choices={[
                                    {value: 'workflow', text: 'Specific Workflow', label: 'Specific Workflow'},
                                    {value: 'general', text: 'General', label: 'General'},
                                ]}
                                value={formState.helpType}
                                onChange={handleChange('helpType')}
                            />
                        </div>
                        {formState.helpType === 'workflow' &&
                        <div className="form-group text-left">
                            <Select
                                required={true}
                                onChange={handleChange('workflow')}
                                // @ts-ignore
                                options={workflows}
                                defaultValue={[]}
                                value={formState.workflow}
                                placeholder="Select a Workflow"
                            />
                        </div>
                        }
                        {formState.helpType &&
                        <div className="form-group">
                            <textarea
                                className="form-control"
                                rows={10}
                                placeholder="Describe your problem"
                                onChange={handleChange('message')}
                                value={formState.message}
                            />
                        </div>
                        }
                    </form>
                </div>
                <div className="modal-footer">
                    <ActivityButton type="button"
                                    className="btn btn-primary"
                                    isActive={formIsSubmitting}
                                    disabled={!canSubmit}
                                    onClick={() => {
                                        sendHelp();
                                    }}
                    >Submit
                    </ActivityButton>
                </div>
            </Modal>}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    isShowingModal: getFeedbackModalIsShowing(state)
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchFeedbackShowModal: () => {
            dispatch(showFeedbackModal());
        },
        dispatchFeedbackHideModal: () => {
            dispatch(hideFeedbackModal());
        }
    };
};

const ConnectedFeedback = connect(mapStateToProps, mapDispatchToProps)(Feedback);
export {ConnectedFeedback as Feedback};